import { NavLink, useMatch } from "react-router-dom";
import { Link, LinkProps } from "@mui/material";
import classNames from "classnames";

import { StyledIconLink } from "./IconLink.styles";

export type IconLinkProps = LinkProps & {
  /**
   * The icon to be displayed in the link.
   * This should be a React component that renders an icon.
   */
  Icon: React.ElementType;
  /**
   * The active icon if it should be different when the link is active.
   * @default Icon
   */
  ActiveIcon?: React.ElementType;
  /**
   * The URL to navigate to when the link is clicked.
   */
  to: string;
  /**
   * Optional function to determine if the link is active.
   * If provided, this overrides the default useMatch behavior.
   */
  isActive?: () => boolean;
  /**
   * The children to be rendered inside the link, i.e. the label or text.
   */
  children?: React.ReactNode;
};

export const IconLink: React.FC<IconLinkProps> = ({ children, Icon, ActiveIcon = Icon, to, isActive, ...rest }: IconLinkProps) => {
  const matches = useMatch(to);
  const active = isActive ? isActive() : matches !== null;
  const icon = active ? <ActiveIcon /> : <Icon />;

  return (
    <StyledIconLink>
      <Link component={NavLink} to={to} {...rest} className={classNames({ active: active ? "active" : "" }, rest.className)}>
        <span className="icon">{icon}</span>
        {children}
      </Link>
    </StyledIconLink>
  );
};
